import { ApiError, ForbiddenError, NetworkError } from "@/api/errors";

import { StartApplicationError } from "./use-start-application";

/** 起票の失敗を、画面が文言を選べる形にしたもの。 */
export type StartErrorView = {
  /**
   * 失敗の理由。`model-invalid` はモデルの BPMN に実行できない箇所があるもの、
   * `failed` は理由を特定できないもの。
   */
  readonly reason: "forbidden" | "model-invalid" | "failed";
  /** 失敗した手順。手順の分からない失敗では null。 */
  readonly stage: StartApplicationError["stage"] | null;
  /** backend の説明（英語のまま）。権限なし・本文に説明が無い失敗では null。 */
  readonly detail: string | null;
  /** backend の error_code（本文に無ければ `http_<status>`）。backend の応答でない失敗では null。 */
  readonly errorCode: string | null;
  /** 原因の要素 id（backend の `task_id`。通常は BPMN の要素 id）。 */
  readonly elementId: string | null;
  /** 押し直してよいか（通る見込みがあり、案件を二重に作らない）。 */
  readonly retryable: boolean;
  /** 作れた案件。作成より後の手順で失敗したときだけ。 */
  readonly caseId: number | null;
};

/**
 * BPMN の不備とみなす error_code（SpiffArena v2.3.0 で実物を確認）。
 * - `unexpected_workflow_exception`: SpiffArena が未処理のワークフロー例外を包む汎用の
 *   コード。作成時はワークフローの組み立ての失敗（例: 開始イベントの id が予約名 `Start`
 *   と衝突）で、実行時はスクリプト・条件式の失敗も含むため、不備とみなすのは近似
 * - `process_instance_validation_error`: BPMN の解析で弾かれた
 *   （例: 条件も既定フローも無いゲートウェイの線）
 */
const MODEL_INVALID_CODES: ReadonlySet<string> = new Set([
  "unexpected_workflow_exception",
  "process_instance_validation_error",
]);

/**
 * 押し直しで通る見込みがある status。SpiffArena は ApiError 以外の例外をすべて 500 に
 * するので、500 は決定的な不具合とみなして含めない。
 */
const TRANSIENT_STATUSES: ReadonlySet<number> = new Set([502, 503, 504]);

/**
 * 起票の失敗を分類する。
 *
 * 失敗全般を 1 つの文言にまとめると、BPMN の不備でも利用者が原因を知る手段が無い
 * （#43 の発端）。backend が返した説明はそのまま見せる（権限なしは除く）。押し直しを
 * 促すのは、案件ができていない一時的な失敗（通信失敗・502 / 503 / 504）だけ。
 */
export function describeStartError(error: unknown): StartErrorView {
  // 3 つの API 呼び出しは StartApplicationError に包むので、それ以外は作成の後
  // （応答の解釈・遷移）で起きた失敗。案件ができている可能性があるので押し直させない
  if (!(error instanceof StartApplicationError)) {
    return {
      reason: "failed",
      stage: null,
      detail: null,
      errorCode: null,
      elementId: null,
      retryable: false,
      caseId: null,
    };
  }

  const { cause, stage, caseId } = error;
  // 案件が作れた後に押し直すと 2 件目が作られる。押し直してよいのは作成で失敗したときだけ
  const beforeCreated = caseId === null;
  if (!(cause instanceof ApiError)) {
    return {
      reason: "failed",
      stage,
      detail: null,
      errorCode: null,
      elementId: null,
      // 想定外の応答（id の無い応答など）は、案件ができたかどうか分からない
      retryable: beforeCreated && cause instanceof NetworkError,
      caseId,
    };
  }

  const reason = reasonOf(cause);
  return {
    reason,
    stage,
    // ApiError.message は本文に無いとき `400 Bad Request` のような補完値になり、理由として
    // 見せても情報が無いので、本文から直接読む
    detail:
      reason === "forbidden" ? null : stringFromBody(cause.body, "message"),
    errorCode: cause.errorCode,
    elementId: stringFromBody(cause.body, "task_id"),
    retryable:
      beforeCreated &&
      reason === "failed" &&
      TRANSIENT_STATUSES.has(cause.status),
    caseId,
  };
}

function reasonOf(cause: ApiError): StartErrorView["reason"] {
  if (cause instanceof ForbiddenError) return "forbidden";
  if (MODEL_INVALID_CODES.has(cause.errorCode)) return "model-invalid";
  return "failed";
}

/** 本文の文字列項目（空文字は無いものとみなす）。 */
function stringFromBody(body: unknown, key: string): string | null {
  if (body === null || typeof body !== "object") return null;
  const value = (body as Record<string, unknown>)[key];
  return typeof value === "string" && value !== "" ? value : null;
}
